import ModalHeader from "../../../../../Components/ModalHeader/ModalHeader";
import Accordion from "../../../../../Components/Accordion/Accordion";
import { useUploadProcessProvider } from "../../../../../Providers/UploadProcessProvider";
import { useUploadQueueProvider } from "../../../../../Providers/UploadQueueProvider";
import { useUploadIntoQueueProvider } from "../../../../../Providers/UploadIntoQueueProvider";
import ModalScrollbar from "../../../../../Components/ModalScrollbar/ModalScrollbar"
import { useModalProvider } from "../../../../../Providers/ModalProvider";
import { UploadFilesModal, DataSummaryDiv, ModalFooter, UploadStepHeader } from "../../../../../Constants/StyledComponents";
import UploadSummaryData from './UploadSummaryData';
import { useState } from 'react';
import { InfoMessage } from "../../../../../Constants/StyledComponents";
import { MobergButton } from "../../../../../Components/MobergButton/MobergButton";
import { MobergTheme } from "../../../../../Components/MobergThemes/MobergColors";
import { MobergButtonVariant } from "../../../../../Components/MobergButton/MobergButton";
import { MobergButtonShape } from "../../../../../Components/MobergButton/MobergButton";
import { useOnMount } from "../../../../../Hooks/useOnMount";
import { calculateUploadSize } from "../UploadHelperFunctions";
import { useEnvironmentVariablesProvider } from "../../../../../Providers/EnvironmentVariablesProvider";
import FileType from "./FileType";

const UploadSummaryPatient = ({
    setToPrevWindow,
    setToNextWindow,
}) => {
    const uploadProcessProvider = useUploadProcessProvider()
    const uploadIntoQueueProvider = useUploadIntoQueueProvider()
    const uploadQueueProvider = useUploadQueueProvider()
    const environmentVariablesProvider = useEnvironmentVariablesProvider()

    const handleBack = () => {
        setToPrevWindow(true);
    }

    /**
     * Filters an array of files and folders, removing empty folders and files with size 0.
     * Un-nest nested folder [[],[],[]] into a single array [].
     * @param {Array} folder - An array containing files and folders.
     * @returns {Array} - A filtered, flatten array containing files and non-empty folders.
     */
    const filterFiles = (folder) => {
        const flattenedFiles = folder.reduce((accumulator, current) => {
            return accumulator.concat(current);
        }, []);
        return flattenedFiles.filter((file) => {
            if (file.type === 'folder') {
                return file.children.length > 0;
            } else {
                return file.size > 0;
            }
        });
    }

    const id = Date.now();

    /**
     * This function creates and prepares an upload data object for later upload.
     * It collects various pieces of information and organizes them into an object then transfer them to a context provider for later transfer to the upload queue.
     */
    const createUploadData = () => {
        let uploadData = {
            cns: filterFiles(uploadProcessProvider.cnsFolders),
            medications: uploadProcessProvider.medicationsFiles,
            natus: filterFiles(uploadProcessProvider.natusFolders),
            edf: uploadProcessProvider.edfFiles,
            patient_id: uploadProcessProvider.patientID,
            site_id: uploadProcessProvider.selectedSitePrimaryKey,
            project_id: uploadProcessProvider.selectedProjectPrimaryKey,
            id: id,
            cns_directory_names: uploadProcessProvider.cnsDirectoryNames.map(obj => obj.name),
            natus_directory_names: uploadProcessProvider.natusDirectoryNames.map(obj => obj.name),
            medications_file_names: uploadProcessProvider.medicationsFileNames.map(obj => obj.name),
            edf_file_names: uploadProcessProvider.edfFileNames.map(obj => obj.name)
        }

        uploadIntoQueueProvider.setUploadData(uploadData);
        uploadIntoQueueProvider.setForm(uploadProcessProvider.submittedForm)
        uploadData = null;
    }



    const [totalUploadSize, setTotalUploadSize] = useState('') // stores the upload size for all 3 data types (Moberg Data, Natus EEG, and Medications)
    const [mobergUploadMetric, setMobergUploadMetric] = useState('') // stores the upload size & number of directories for Moberg Data
    const [natusUploadMetric, setNatusUploadMetric] = useState('') // stores the upload size & number of directories for Natus EEG
    const [medicationsUploadMetric, setMedicationsUploadMetric] = useState('') // stores the upload size & number of files for Medications
    const [edfUploadMetric, setEDFUploadMetric] = useState('')




    /**
     * This function creates and prepares an object containing the names of the directories that already exist in the upload queue.
     * It collects id, directory names and organizes them into an object then transfer them to a context provider for later validation within the queue.
     * This is used to prevent the user from uploading the same data twice.
     */
    const createExistingDirectoryNames = () => {
        const currentUploadsString = sessionStorage.getItem('currentUploads')
        const patient_id = uploadProcessProvider.patientID
        const site_id = uploadProcessProvider.selectedSitePrimaryKey
        const project_id = uploadProcessProvider.selectedProjectPrimaryKey

        const cns_directory_names = uploadProcessProvider.cnsDirectoryNames.map(obj => obj.name)
        const natus_directory_names = uploadProcessProvider.natusDirectoryNames.map(obj => obj.name)
        const medications_file_names = uploadProcessProvider.medicationsFileNames.map(obj => obj.name)

        if (currentUploadsString) {
            const currentUploads = JSON.parse(currentUploadsString)

            if (Object.keys(currentUploads).includes(`${site_id}-${project_id}-${patient_id}`)) {
                const currentDataObjects = currentUploads[`${site_id}-${project_id}-${patient_id}`]

                currentUploads[`${site_id}-${project_id}-${patient_id}`] = [...currentDataObjects, ...cns_directory_names, ...natus_directory_names, ...medications_file_names]
            } else {
                currentUploads[`${site_id}-${project_id}-${patient_id}`] = [...cns_directory_names, ...natus_directory_names, ...medications_file_names]
            }

            sessionStorage.setItem('currentUploads', JSON.stringify(currentUploads))
        } else {
            const currentUploads = {}

            currentUploads[`${site_id}-${project_id}-${patient_id}`] = [...cns_directory_names, ...natus_directory_names, ...medications_file_names]

            sessionStorage.setItem('currentUploads', JSON.stringify(currentUploads))
        }
    }

    /**
    * This `useEffect` is responsible for calculating and setting various upload metrics
    * based on table data provided by the `uploadProcessProvider` to render a "Summary".
    * Calculate the upload metric for a given table of files.
    * @param {Array} tableData - The table data representing files.
    * @returns {string} - A string representing the calculated upload metric.
    */
    useOnMount(() => {
        // Calculate and set upload metrics for CNS, Natus, and Medications tables
        setMobergUploadMetric(calculateUploadSize(uploadProcessProvider.cnsTableData));
        setNatusUploadMetric(calculateUploadSize(uploadProcessProvider.natusTableData));
        setMedicationsUploadMetric(calculateUploadSize(uploadProcessProvider.medicationsTableData));
        setEDFUploadMetric(calculateUploadSize(uploadProcessProvider.edfTableData))
        // Calculate and set the total upload size metric for all tables combined
        setTotalUploadSize(calculateUploadSize([...uploadProcessProvider.cnsTableData, ...uploadProcessProvider.natusTableData, ...uploadProcessProvider.medicationsTableData, ...uploadProcessProvider.edfTableData]));
    })

    const { close } = useModalProvider()

    /**
     * This function initiates the upload process.
     * It sets the selected site ID and patient ID to the upload queue provider.
     * It creates and prepares an upload data object for later upload.
     * It creates and prepares an object containing the names of the directories that already exist in the upload queue.
     * It sets the upload queue to open and the upload summary to close.
     * It resets the upload process provider cache.
     * It sets the upload queue to close.
     * @returns {void}
     * @example
     **/
    function handleNext() {
        if (uploadQueueProvider.selectedSiteID === '' && uploadQueueProvider.patientID === '') {
            uploadQueueProvider.setSelectedSiteID(uploadProcessProvider.selectedSiteID)
            uploadQueueProvider.setSelectedSiteName(uploadProcessProvider.selectedSiteName)
            uploadQueueProvider.setPatientID(uploadProcessProvider.patientID)
            uploadQueueProvider.setSitePrimaryKey(uploadProcessProvider.selectedSitePrimaryKey)
            uploadQueueProvider.setProjectPrimaryKey(uploadProcessProvider.selectedProjectPrimaryKey)
        }
        uploadQueueProvider.setQueuedSiteID(uploadProcessProvider.selectedSiteID)
        uploadQueueProvider.setQueuedPatientID(uploadProcessProvider.patientID)
        uploadQueueProvider.setQueuedSiteName(uploadProcessProvider.selectedSiteName)
        createUploadData();
        createExistingDirectoryNames();
        setToNextWindow(true);
        uploadIntoQueueProvider.setOpenQueue(true);
        uploadProcessProvider.resetCache();
        uploadQueueProvider.setCloseQueue(false)
    }

    // Summary of the upload inputs from the user using the accordions
    const accordionData = {
        ...(!environmentVariablesProvider.environmentVariables.CNS || environmentVariablesProvider.environmentVariables.FORM
            ? {}
            : {
                'mobergData': {
                    'label': "Moberg CNS (patient.info)",
                    'innerContent': <UploadSummaryData label={"Moberg Data"} />,
                    'description': mobergUploadMetric
                }
            }),
        ...(!environmentVariablesProvider.environmentVariables.NATUS
            ? {}
            : {
                'natusEEG': {
                    'label': FileType.NATUS,
                    'innerContent': <UploadSummaryData label={FileType.NATUS} />,
                    'description': natusUploadMetric
                }
            }
        ),
        ...(!environmentVariablesProvider.environmentVariables.MEDICATIONS || environmentVariablesProvider.environmentVariables.FORM
            ? {}
            : {
                'medications': {
                    'label': FileType.MEDICATIONS + " (.csv, .xlsx, .xls)",
                    'innerContent': <UploadSummaryData label={FileType.MEDICATIONS} />,
                    'description': medicationsUploadMetric
                }
            }),
        ...(!environmentVariablesProvider.environmentVariables.EDF || environmentVariablesProvider.environmentVariables.FORM
            ? {}
            : {
                'EDF': {
                    'label': FileType.EDF + " (.edf)",
                    'innerContent': <UploadSummaryData label={FileType.EDF} />,
                    'description': edfUploadMetric
                }
            }
        ),
        ...(!environmentVariablesProvider.environmentVariables.FORM
            ? {}
            : {
                'form': {
                    'label': 'Form',
                    'innerContent': <UploadSummaryData label={"Form"} />,
                }
            })
    }


    return (<>
        <div>
            <UploadFilesModal>
                <ModalHeader headerText={"Data Summary"} resetCache={uploadProcessProvider.resetCache} />
                <ModalScrollbar>
                    <UploadStepHeader>5. Confirm data for upload</UploadStepHeader>

                    <div style={{ marginLeft: "auto", marginRight: "auto", textAlign: "center" }}>
						<p style={{fontWeight: "600", marginBottom: "5px"}}>Site: {uploadProcessProvider.selectedSiteName}
						</p>
					</div>

					<div style={{ marginLeft: "auto", marginRight: "auto", textAlign: "center" }}>
						<p style={{fontWeight: "600", marginBottom: "5px" }}>Project: {uploadProcessProvider.selectedProjectName}</p>
					</div>

					<div style={{ marginLeft: "auto", marginRight: "auto", textAlign: "center" }}>
						<p style={{fontWeight: "600", marginBottom: "15px" }}>Patient: {uploadProcessProvider.patientID}</p>
					</div>

                    {environmentVariablesProvider.environmentVariables.CNS && uploadProcessProvider.cnsTableData.length > 0 && environmentVariablesProvider.environmentVariables.ENCRYPT && (
                        <InfoMessage style={{ marginBottom: '16px', marginTop: '-10px' }}>
                            <p style={{ color: 'var(--color-accent-interactive)' }}>
                                <strong>Info! </strong>The patient.info and admission.info files in your CNS directories will be encrypted on upload.
                            </p>
                        </InfoMessage>
                    )}

                    <div style={{ display: 'flex' }}>
                        <h1 style={{ marginBottom: "10px", color: "var(--color-accent-interactive)" }}>Review Uploads</h1>
                    </div>

                    <Accordion data={accordionData} />

                    <div>
                        <h1 style={{ marginBottom: "10px", color: "var(--color-accent-interactive)", marginTop: "10px" }}>Data Summary</h1>

                        <DataSummaryDiv>
                            <h2>Total size of upload:</h2>
                            <p>{totalUploadSize}</p>
                        </DataSummaryDiv>

                    </div>
                </ModalScrollbar>
                <hr style={{ border: "1px solid var(--color-border-default)", width: "100%", margin: '0px auto' }} />
                <ModalFooter>
                    <MobergButton
                        theme={MobergTheme.BLUE}
                        variant={MobergButtonVariant.OUTLINE}
                        shape={MobergButtonShape.WIDE}
                        onClick={handleBack}
                    >
                        Back
                    </MobergButton>

                    <MobergButton
                        theme={MobergTheme.BLUE}
                        variant={MobergButtonVariant.FILLED}
                        shape={MobergButtonShape.WIDE}
                        onClick={handleNext}
                    >
                        Upload
                    </MobergButton>
                </ModalFooter>
            </UploadFilesModal>
        </div>
    </>
    )
}

export default UploadSummaryPatient;